import type { EmailDocumentV1 } from "@repo/email-content";

export type OsCampaignAudienceTag = "Buyers" | "Sellers" | "Investors" | "Owners";

export type OsCampaignLayout =
  | "MULTIPLE_LISTINGS"
  | "SINGLE_LISTING"
  | "MESSAGE_ONLY";

export type OsCampaignReplyAction =
  | "NONE"
  | "BUYER_ONBOARDING"
  | "SELLER_ONBOARDING"
  | "NOTIFY_BROKER";

export type OsCampaignPersona = "BROKER" | "ACQUIRER";

export type OsCampaignPreset = {
  id: string;
  /** Intent-first label shown on template cards and as the draft campaign name. */
  name: string;
  /** One-line outcome for the card — who it's for and what happens. */
  intentSummary: string;
  description: string;
  audienceTag?: OsCampaignAudienceTag;
  layout: OsCampaignLayout;
  replyAction: OsCampaignReplyAction;
  defaultName: string;
  defaultSubject: string;
  defaultTitle: string;
  document: EmailDocumentV1;
  ctaHint: string;
};

function messageDocument(markdown: string): EmailDocumentV1 {
  return {
    version: 1,
    blocks: [{ id: "text-1", type: "text", markdown }],
  };
}

/**
 * Presets are ordered by broker intent: write freely → win buyers → win
 * sellers/owners. Card copy names the job to be done, not the email layout.
 */
export const OS_CAMPAIGN_PRESETS: OsCampaignPreset[] = [
  {
    id: "general-message",
    name: "Write your own note",
    intentSummary: "A personal email with no listings",
    description:
      "Send your own message. No listings step — replies are tracked only.",
    layout: "MESSAGE_ONLY",
    replyAction: "NONE",
    defaultName: "Write your own note",
    defaultSubject: "A quick note for you",
    defaultTitle: "",
    document: messageDocument(
      "Hi {{name}},\n\nI wanted to share a quick update with you.\n\n",
    ),
    ctaHint: "Replies are tracked only — no automated follow-up",
  },
  {
    id: "buyer-opportunities",
    name: "Share a shortlist",
    intentSummary: "A few matched businesses for buyers",
    description:
      "Send buyers a shortlist of businesses that fit. Replies start buyer onboarding.",
    audienceTag: "Buyers",
    layout: "MULTIPLE_LISTINGS",
    replyAction: "BUYER_ONBOARDING",
    defaultName: "Share a shortlist",
    defaultSubject: "{{firstName}}, a few businesses worth a look",
    defaultTitle: "Businesses that may fit you",
    document: messageDocument(
      "Hi {{name}},\n\nI put together a shortlist of businesses that align with what you're looking for. Take a look below — reply to this email and I'll help you get started with buyer onboarding.\n",
    ),
    ctaHint: "Replies start buyer onboarding",
  },
  {
    id: "buyer-spotlight",
    name: "Highlight one business",
    intentSummary: "Put a single opportunity in front of buyers",
    description:
      "Focus buyers on one listing. Replies attach interest and start onboarding.",
    audienceTag: "Buyers",
    layout: "SINGLE_LISTING",
    replyAction: "BUYER_ONBOARDING",
    defaultName: "Highlight one business",
    defaultSubject: "A business I think you'll want to see",
    defaultTitle: "Featured opportunity",
    document: messageDocument(
      "Hi {{name}},\n\nI wanted to put this one in front of you personally. If it looks interesting, reply to this email and we'll start your buyer onboarding with this listing attached.\n",
    ),
    ctaHint: "Replies attach listing interest and start buyer onboarding",
  },
  {
    id: "buyer-just-listed",
    name: "Announce a new listing",
    intentSummary: "Give buyers an early look",
    description:
      "Alert buyers that something just came to market. Replies start onboarding.",
    audienceTag: "Buyers",
    layout: "SINGLE_LISTING",
    replyAction: "BUYER_ONBOARDING",
    defaultName: "Announce a new listing",
    defaultSubject: "Just listed — early look for you",
    defaultTitle: "New to market",
    document: messageDocument(
      "Hi {{name}},\n\nThis opportunity just came to market. If you'd like an early conversation, reply to this email and I'll kick off buyer onboarding for you.\n",
    ),
    ctaHint: "Replies start buyer onboarding for this listing",
  },
  {
    id: "investor-portfolio",
    name: "Pitch investors",
    intentSummary: "Several businesses for acquisitive buyers",
    description:
      "Send investors or serial acquirers a multi-asset pass. Replies start buyer onboarding.",
    audienceTag: "Investors",
    layout: "MULTIPLE_LISTINGS",
    replyAction: "BUYER_ONBOARDING",
    defaultName: "Pitch investors",
    defaultSubject: "{{firstName}}, portfolio opportunities for you",
    defaultTitle: "Investment opportunities",
    document: messageDocument(
      "Hi {{name}},\n\nHere are several businesses that may fit an investor or portfolio thesis. Reply if you'd like to dig in — I'll start buyer onboarding so we can move quickly.\n",
    ),
    ctaHint: "Replies start buyer onboarding",
  },
  {
    id: "seller-thinking",
    name: "Start a sale conversation",
    intentSummary: "Confidential outreach to owners",
    description:
      "Reach owners who may be considering a sale. Replies start seller onboarding.",
    audienceTag: "Sellers",
    layout: "MESSAGE_ONLY",
    replyAction: "SELLER_ONBOARDING",
    defaultName: "Start a sale conversation",
    defaultSubject: "Considering a sale? Let's talk confidentially",
    defaultTitle: "A confidential conversation",
    document: messageDocument(
      "Hi {{name}},\n\nIf you've been thinking about a sale — even casually — I'd welcome a confidential conversation. Reply to this email and I'll send a short seller onboarding link so we can understand your goals.\n",
    ),
    ctaHint: "Replies start seller onboarding",
  },
  {
    id: "owner-check-in",
    name: "Stay in touch",
    intentSummary: "Light nurture — replies come to you",
    description:
      "Send a relationship note or market update. Replies notify you — no auto-intake.",
    audienceTag: "Owners",
    layout: "MESSAGE_ONLY",
    replyAction: "NOTIFY_BROKER",
    defaultName: "Stay in touch",
    defaultSubject: "Checking in — how can I help?",
    defaultTitle: "A quick check-in",
    document: messageDocument(
      "Hi {{name}},\n\nI wanted to check in and share a brief market note. If anything is on your mind — timing, valuation, or just a second opinion — reply to this email and I'll get back to you personally.\n",
    ),
    ctaHint: "Replies notify you by email",
  },
];

/**
 * Acquirer templates are intentionally separate from broker templates. The
 * workflow is owner outreach and diligence coordination, not listing
 * distribution or seller representation.
 */
export const OS_ACQUIRER_CAMPAIGN_PRESETS: OsCampaignPreset[] = [
  {
    id: "acquirer-owner-introduction",
    name: "Introduce a long-term owner",
    intentSummary: "A respectful first note to a target owner",
    description:
      "Open a confidential acquisition conversation with a business owner. Replies come back to the principal.",
    audienceTag: "Owners",
    layout: "MESSAGE_ONLY",
    replyAction: "NOTIFY_BROKER",
    defaultName: "Introduce a long-term owner",
    defaultSubject: "A thoughtful conversation about your business",
    defaultTitle: "A respectful introduction",
    document: messageDocument(
      "Hello {{name}},\n\nI am reaching out from a long-term investment group that is interested in learning more about owner-led businesses like yours. If you are open to it, I would welcome a private conversation about your goals and whether a transition could ever be a fit.\n",
    ),
    ctaHint: "Replies notify the acquisition principal",
  },
  {
    id: "acquirer-search-thesis",
    name: "Share the search thesis",
    intentSummary: "Explain what your acquisition team is looking for",
    description:
      "Keep referral partners and capital partners aligned on the current search focus.",
    audienceTag: "Investors",
    layout: "MESSAGE_ONLY",
    replyAction: "NOTIFY_BROKER",
    defaultName: "Share the search thesis",
    defaultSubject: "Our 2026 acquisition focus",
    defaultTitle: "What we are looking for",
    document: messageDocument(
      "Hello {{name}},\n\nOur current search is focused on durable, owner-operated businesses with repeat customers, strong teams, and a thoughtful transition path. I wanted to share the criteria we are using as we evaluate new conversations across the Southeast.\n",
    ),
    ctaHint: "Replies notify the acquisition principal",
  },
  {
    id: "acquirer-diligence-update",
    name: "Keep diligence moving",
    intentSummary: "Send a clear progress note on an active target",
    description:
      "Give owners, lenders, and advisors a concise update while diligence is underway.",
    audienceTag: "Investors",
    layout: "MESSAGE_ONLY",
    replyAction: "NOTIFY_BROKER",
    defaultName: "Keep diligence moving",
    defaultSubject: "Diligence progress update",
    defaultTitle: "Diligence progress",
    document: messageDocument(
      "Hello {{name}},\n\nWe have moved the review into the next diligence phase. The operating profile remains compelling, and we are working through the remaining customer concentration and transition-plan questions with the owner.\n\nI will share the refreshed sources-and-uses view once those materials are reconciled.\n",
    ),
    ctaHint: "Replies notify the acquisition principal",
  },
  {
    id: "acquirer-capital-update",
    name: "Update capital partners",
    intentSummary: "Keep lenders and investors current on a target",
    description:
      "Share a focused underwriting update without exposing sensitive diligence materials.",
    audienceTag: "Investors",
    layout: "MESSAGE_ONLY",
    replyAction: "NOTIFY_BROKER",
    defaultName: "Update capital partners",
    defaultSubject: "Target review — sources and uses update",
    defaultTitle: "A focused diligence update",
    document: messageDocument(
      "Hello {{name}},\n\nThe target review is progressing and the current underwriting range remains anchored by recurring revenue and an experienced operating team. I am closing the remaining diligence items before circulating the updated sources-and-uses view.\n",
    ),
    ctaHint: "Replies notify the acquisition principal",
  },
];

export function getOsCampaignPresets(
  persona: OsCampaignPersona = "BROKER",
): OsCampaignPreset[] {
  return persona === "ACQUIRER"
    ? OS_ACQUIRER_CAMPAIGN_PRESETS
    : OS_CAMPAIGN_PRESETS;
}

export function getOsCampaignPreset(
  presetId: string,
): OsCampaignPreset | undefined {
  return [...OS_CAMPAIGN_PRESETS, ...OS_ACQUIRER_CAMPAIGN_PRESETS].find(
    (preset) => preset.id === presetId,
  );
}

export function replyActionLabel(action: OsCampaignReplyAction): string {
  switch (action) {
    case "NONE":
      return "Track replies only";
    case "BUYER_ONBOARDING":
      return "Buyer onboarding";
    case "SELLER_ONBOARDING":
      return "Seller onboarding";
    case "NOTIFY_BROKER":
      return "Notify broker";
  }
}
